import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  useTheme,
  InputBase
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import BookIcon from '@mui/icons-material/Book';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TemplateIcon from '@mui/icons-material/ViewModule';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LOGO_PATH } from '../config';

function Navbar() {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const { isAuthenticated, signOut, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleSignOut = async () => {
    await signOut();
    handleCloseUserMenu();
    navigate('/signin');
  };

  const pages = [
    { title: 'Home', path: '/', icon: <HomeIcon sx={{ mr: 1 }} /> },
    { title: 'Blog', path: '/blog', icon: <BookIcon sx={{ mr: 1 }} /> },
    { title: 'Offers', path: '/offers', icon: <LocalOfferIcon sx={{ mr: 1 }} /> },
    { title: 'Templates', path: '/offer-templates', icon: <TemplateIcon sx={{ mr: 1 }} /> },
    { title: 'Brands', path: '/brands', icon: <BusinessIcon sx={{ mr: 1 }} /> },
    { title: 'Contact', path: '/contact', icon: <ContactSupportIcon sx={{ mr: 1 }} /> }
  ];

  const adminPages = [
    { title: 'Users', path: '/users', icon: <GroupIcon sx={{ mr: 1 }} /> }
  ];

  // Show admin pages only for admin users
  const allPages = isAuthenticated 
    ? user?.role === 'admin' 
      ? [...pages, ...adminPages] 
      : pages
    : pages;

  const settings = [
    { title: 'Profile', path: '/profile', onClick: handleCloseUserMenu },
    { title: 'Sign out', onClick: handleSignOut }
  ];

  return (
    <AppBar position="sticky">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo for larger screens */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <img
              src={LOGO_PATH}
              alt="Logo"
              style={{
                height: '40px',
                width: '40px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                console.error('Logo failed to load:', e);
                e.target.style.display = 'none';
              }}
            />
          </Box>

          {/* Mobile menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {allPages.map((page) => (
                <MenuItem 
                  key={page.title} 
                  onClick={handleCloseNavMenu}
                  component={RouterLink}
                  to={page.path}
                  selected={location.pathname === page.path}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {page.icon}
                    <Typography textAlign="center">{page.title}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Logo for mobile */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <img
              src={LOGO_PATH}
              alt="Logo"
              style={{
                height: '35px',
                width: '35px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                console.error('Mobile logo failed to load:', e);
                e.target.style.display = 'none';
              }}
            />
          </Box>

          {/* Search bar */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexGrow: 1,
              maxWidth: 600,
              mx: 4,
              position: 'relative',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              borderRadius: 20,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Box
              sx={{
                padding: '0 16px',
                height: '100%',
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </Box>
            <InputBase
              placeholder="Search..."
              sx={{
                color: 'text.primary',
                width: '100%',
                '& .MuiInputBase-input': {
                  padding: '8px 8px 8px 48px',
                  width: '100%',
                },
              }}
            />
          </Box>

          {/* Desktop menu */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            {allPages.map((page) => (
              <Button
                key={page.title}
                component={RouterLink}
                to={page.path}
                onClick={handleCloseNavMenu}
                sx={{ 
                  color: location.pathname === page.path ? theme.palette.primary.main : 'text.primary',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    backgroundColor: 'rgba(29, 161, 242, 0.1)',
                  }
                }}
              >
                {page.icon}
                {page.title}
              </Button>
            ))}
          </Box>

          {/* User menu */}
          <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
            {isAuthenticated ? (
              <>
                <IconButton sx={{ color: 'text.primary' }}>
                  <NotificationsIcon />
                </IconButton>
                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>U</Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {settings.map((setting) => (
                    <MenuItem
                      key={setting.title}
                      onClick={setting.onClick}
                      component={setting.path ? RouterLink : undefined}
                      to={setting.path}
                    >
                      <Typography textAlign="center">{setting.title}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  component={RouterLink} 
                  to="/signin"
                  variant="text"
                  sx={{ 
                    color: 'text.primary',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      backgroundColor: 'rgba(29, 161, 242, 0.1)',
                    }
                  }}
                >
                  Sign In
                </Button>
                <Button 
                  component={RouterLink} 
                  to="/signup"
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    }
                  }}
                >
                  Sign Up
                </Button>
              </Box>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default Navbar; 